import { useCallback, useEffect, useRef, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { DiagnosticOutDTO, DiagnosticSeverityEnum, RuleAst, RuleDetailOutDTO, RuleEditModeEnum, SaveRuleInDTO } from '@api/ringil-api.v2';
import { emptyAst, RuleHeader, TypedRuleAst } from '../ruleAst';
import { useRuleApi } from '../useRuleApi';

export type RuleEditorMode = 'TEXT' | 'BUILDER';

/**
 * One rule, two panes, one truth.
 *
 * `sourceText` is the stored state and the AST is derived, but only one pane *owns* the rule at a
 * time, which is what keeps the two from oscillating:
 *
 * - TEXT mode owns `sourceText`. Typing debounces into `validate`, which replaces the AST, the
 *   header and the diagnostics. The text is never rewritten from `validate.formatted` while the
 *   user types; that only happens on the explicit Format action.
 * - BUILDER mode owns `ast`. A builder edit marks the AST dirty and debounces into `format`,
 *   which regenerates `sourceText`. The text pane is read-only meanwhile.
 *
 * The dirty flag matters: without it, the AST that `validate` just produced would immediately be
 * sent back to `format`, costing a round trip on every load and every mode switch.
 */
const VALIDATE_DEBOUNCE_MS = 400;
const FORMAT_DEBOUNCE_MS = 300;

export interface RuleEditorState {
  editorMode: RuleEditorMode;
  sourceText: string;
  ast?: TypedRuleAst;
  /** The last AST that parsed cleanly, so the builder has something to fall back on. */
  lastGoodAst?: TypedRuleAst;
  header: RuleHeader;
  diagnostics: DiagnosticOutDTO[];
  /** The canonical rendering of what was last parsed, for the Format action. */
  formatted?: string;
  /** True once the source has been typed in and not yet regenerated from the builder. */
  handEdited: boolean;
  /** True when the AST was changed by the builder and the source has not caught up yet. */
  astDirty: boolean;
  busy: boolean;
}

/** What the builder shows before the first `validate` comes back with the rule's real header. */
export const defaultHeader = (tr: (key: string, defaultText?: string) => string): RuleHeader => ({
  name: tr('AiDispatcher.newRule', 'New rule'),
  enabled: true,
  priority: 100,
});

export const hasErrors = (diagnostics: DiagnosticOutDTO[]): boolean => diagnostics.some(diagnostic => diagnostic.diagnosticSeverity === DiagnosticSeverityEnum.ERROR);

export const useRuleEditor = (initialSourceText: string, onPersist?: (sourceText: string) => void, companyId?: string) => {
  // Company section or admin section — the hook picks the right endpoints from companyId.
  const { validate, format, saveRule } = useRuleApi(companyId);
  const { tr } = useTranslationLgs();

  const [state, setState] = useState<RuleEditorState>(() => ({
    // The builder is the default view: most rules are written by picking facts and actions, and
    // the text pane is the power-user escape hatch rather than the other way round.
    editorMode: 'BUILDER',
    sourceText: initialSourceText,
    header: defaultHeader(tr),
    diagnostics: [],
    handEdited: false,
    astDirty: false,
    busy: false,
  }));

  /**
   * Responses can land out of order — a slow validate for an old keystroke after a fast one for
   * the current text. Only the newest request may write to state.
   */
  const requestSeq = useRef(0);

  const runValidate = useCallback(
    (sourceText: string) => {
      if (!sourceText.trim()) {
        setState(previous => ({ ...previous, ast: undefined, formatted: undefined, diagnostics: [], busy: false }));
        return;
      }
      const seq = ++requestSeq.current;
      setState(previous => ({ ...previous, busy: true }));
      validate(
        { sourceText },
        data => {
          if (seq !== requestSeq.current) return;
          const parsed = (data.ast as unknown as TypedRuleAst) ?? undefined;
          setState(previous => ({
            ...previous,
            // A rule that failed to parse yields no AST; dropping the stale one keeps the builder
            // from showing a tree that no longer corresponds to the text.
            ast: parsed,
            lastGoodAst: parsed ?? previous.lastGoodAst,
            formatted: data.formatted,
            header: {
              name: data.name ?? previous.header.name,
              enabled: data.enabled ?? previous.header.enabled,
              priority: data.priority ?? previous.header.priority,
            },
            diagnostics: data.diagnostics ?? [],
            busy: false,
          }));
        },
        {
          onError: (_error, showStandardError) => {
            showStandardError();
            if (seq === requestSeq.current) setState(previous => ({ ...previous, busy: false }));
          },
        },
      );
    },
    [validate],
  );

  const runFormat = useCallback(
    (ast: TypedRuleAst, header: RuleHeader) => {
      const seq = ++requestSeq.current;
      setState(previous => ({ ...previous, busy: true }));
      format(
        { ...header, ast: ast as unknown as RuleAst },
        data => {
          if (seq !== requestSeq.current) return;
          setState(previous => ({
            ...previous,
            sourceText: data.sourceText,
            formatted: data.sourceText,
            diagnostics: data.diagnostics ?? [],
            // Regenerated source is by definition no longer hand-edited, and the AST has caught up.
            handEdited: false,
            astDirty: false,
            busy: false,
          }));
          onPersist?.(data.sourceText);
        },
        {
          onError: (_error, showStandardError) => {
            showStandardError();
            if (seq === requestSeq.current) setState(previous => ({ ...previous, busy: false, astDirty: false }));
          },
        },
      );
    },
    [format, onPersist],
  );

  // The rule has to be parsed once on load whichever pane is showing, because the builder opens
  // on the AST and the AST only exists once validate has run.
  const parsedOnMount = useRef(false);
  useEffect(() => {
    if (parsedOnMount.current) return;
    parsedOnMount.current = true;
    runValidate(initialSourceText);
  }, [initialSourceText, runValidate]);

  // TEXT mode: text -> validate -> AST.
  useEffect(() => {
    if (state.editorMode !== 'TEXT') return undefined;
    const handle = setTimeout(() => runValidate(state.sourceText), VALIDATE_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [state.sourceText, state.editorMode, runValidate]);

  // BUILDER mode: a builder edit -> format -> text.
  useEffect(() => {
    if (state.editorMode !== 'BUILDER' || !state.astDirty || !state.ast) return undefined;
    const handle = setTimeout(() => runFormat(state.ast, state.header), FORMAT_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [state.ast, state.header, state.astDirty, state.editorMode, runFormat]);

  const setSourceText = useCallback(
    (sourceText: string) => {
      setState(previous => ({ ...previous, sourceText, handEdited: true }));
      onPersist?.(sourceText);
    },
    [onPersist],
  );

  const setAst = useCallback((updater: (previous: TypedRuleAst) => TypedRuleAst) => {
    setState(previous => (previous.ast ? { ...previous, ast: updater(previous.ast), astDirty: true } : previous));
  }, []);

  const setHeader = useCallback((header: Partial<RuleHeader>) => {
    setState(previous => ({ ...previous, header: { ...previous.header, ...header }, astDirty: true }));
  }, []);

  /**
   * Opens the builder, whatever state the text is in.
   *
   * A rule that does not parse has no AST, so the builder falls back to the last one that did and,
   * failing that, to an empty rule. Either way the AST is marked dirty, so `format` regenerates
   * the source from it — which is what replaces the text that would not parse.
   */
  const enterBuilder = useCallback(() => {
    setState(previous => ({
      ...previous,
      editorMode: 'BUILDER',
      ast: previous.ast ?? previous.lastGoodAst ?? emptyAst(),
      astDirty: true,
    }));
  }, []);

  const enterText = useCallback(() => setState(previous => ({ ...previous, editorMode: 'TEXT' })), []);

  /**
   * Persists the rule.
   *
   * Which representation is sent decides how the backend records `ruleEditMode`, so the pane that
   * owns the rule is the one that is sent: the builder's AST, or the text as written. Saving can
   * fail with diagnostics rather than an error, so the caller is told whether it took.
   */
  const save = useCallback(
    (ruleId: string | undefined, onSaved: (rule?: RuleDetailOutDTO) => void) => {
      setState(previous => ({ ...previous, busy: true }));
      const body: SaveRuleInDTO =
        state.editorMode === 'BUILDER' && state.ast
          ? { ast: state.ast as unknown as RuleAst, name: state.header.name, enabled: state.header.enabled, priority: state.header.priority }
          : { sourceText: state.sourceText };

      saveRule(
        ruleId,
        body,
        data => {
          setState(previous => ({ ...previous, diagnostics: data.diagnostics ?? previous.diagnostics, busy: false }));
          if (data.saved) onSaved(data.rule);
        },
        {
          onError: (_error, showStandardError) => {
            showStandardError();
            setState(previous => ({ ...previous, busy: false }));
          },
        },
      );
    },
    [saveRule, state.editorMode, state.ast, state.header, state.sourceText],
  );

  /** Loads a stored rule into the editor, opening it in the pane it was last edited in. */
  const loadRule = useCallback((rule: RuleDetailOutDTO) => {
    requestSeq.current += 1; // any validate still in flight is for the previous rule
    parsedOnMount.current = true;
    setState({
      editorMode: rule.ruleEditMode === RuleEditModeEnum.TEXT ? 'TEXT' : 'BUILDER',
      sourceText: rule.sourceText,
      ast: rule.ast as unknown as TypedRuleAst,
      lastGoodAst: rule.ast as unknown as TypedRuleAst,
      header: { name: rule.name, enabled: rule.enabled, priority: rule.priority },
      diagnostics: [],
      formatted: rule.sourceText,
      handEdited: false,
      astDirty: false,
      busy: false,
    });
  }, []);

  /** Replaces the source with the canonical rendering of what was parsed. */
  const applyFormatting = useCallback(() => {
    setState(previous => (previous.formatted ? { ...previous, sourceText: previous.formatted, handEdited: false } : previous));
  }, []);

  /**
   * Whether opening the builder would throw away what is currently in the text pane: either the
   * text does not parse (so the builder cannot be showing it) or it was hand-written and the
   * builder will normalise it and drop its comments.
   */
  const builderWillReplaceText = !state.ast || hasErrors(state.diagnostics) || state.handEdited;

  return {
    state,
    save,
    loadRule,
    setSourceText,
    setAst,
    setHeader,
    enterBuilder,
    enterText,
    applyFormatting,
    builderWillReplaceText,
    textParses: Boolean(state.ast) && !hasErrors(state.diagnostics),
    revalidate: () => runValidate(state.sourceText),
  };
};
